/**
 * Module for communicating with Yealink DECT USB headsets through the native Yealink SDK
 * embedded in the Electron desktop shell (v29+).
 * - used when the app runs embedded via IPC; in a browser Yealink.ts (WebHID) is used instead
 * - drives full call control with caller name/number on the device display
 * - pushes a phonebook to the device: local extensions (colleagues) first, then the user's
 *   personal phonebook, then the shared phonebook - capped at 2500 entries
 */

import globalEvents from '@/utilities/GlobalEvents'
import { useMediaDevicesStore } from '@/stores/MediaDevices'
import { useEmployeesStore } from '@/stores/Employees'
import { usePhonebookStore } from '@/stores/Phonebook'
import { useSettingsStore } from '@/stores/Settings'
import logger from '@/utilities/Logger'
import { parseUsbProductId, findMatchingYealinkHid, isYealinkMediaDevice } from './yealinkMatching'
import { MediaDeviceType } from '@/stores/MediaDevices.types'
import { PbxPhonebookContactPhoneNumberType, type PbxPhonebookContact } from '@/TcxApi/PbxPhonebook'
import type { WindowElectronAPI, YealinkCallActionEvent, YealinkContact, YealinkDeviceSnapshot, YealinkEvent } from '@/stores/Electron.types'
import { ref, watch, type Ref } from 'vue'

/** The device phonebook holds at most ~2500 entries and overflow is silent - never push more */
const MAX_PHONEBOOK_ENTRIES = 2500

/** Collapse bursts of phonebook/employee changes into one push to the device */
const PHONEBOOK_SYNC_DEBOUNCE_MS = 5000

/** Events sent to the SipClient when the user presses buttons on the device */
export type YealinkHeadsetEvent = 'ANSWER' | 'HANGUP' | 'REJECT' | 'HOLD' | 'UNHOLD' | 'MUTE' | 'UNMUTE' | 'DTMF'

export default class YealinkElectron {
	static #instance: YealinkElectron // NOSONAR

	/** Is a Yealink headset in use */
	use: Ref<boolean> = ref(false)

	useDeviceName: Ref<string | undefined> = ref(undefined)

	private readonly ipc = (window as unknown as WindowElectronAPI).electronAPI

	/** All currently attached Yealink devices, keyed by SDK deviceId */
	private readonly devices = new Map<number, YealinkDeviceSnapshot>()

	/** The device call control is bound to - the one matching the selected microphone */
	private activeDeviceId?: number = undefined

	/** We own the cid namespace - a simple counter, one cid per logical call */
	private cidCounter = 0

	/** cid of the current call shown on the device, undefined when idle */
	private cid?: number = undefined

	private callState: 'idle' | 'ringing' | 'ringback' | 'active' | 'hold' = 'idle'

	/** Contact currently shown on the device display */
	private currentContact: YealinkContact = { name: '', number: '' }

	/** Contacts as last pushed to the device - used to correlate device-dialed numbers back to full numbers */
	private pushedContacts: YealinkContact[] = []

	/** Fingerprint of the last successful phonebook push, so identical pushes are skipped */
	private lastPushedPhonebook = ''

	private phonebookSyncRunning = false

	private phonebookTimer?: ReturnType<typeof setTimeout> = undefined

	private eventCallback: (event: YealinkHeadsetEvent, value?: string) => void = () => { /** */ }

	/** Last logged bind outcome - bindDevice runs on every media device change, so only log changes */
	private lastBindLog = ''

	private constructor() {
		logger.debug('YealinkElectron: Constructor')

		globalEvents.on('MediaDeviceChange', event => {
			if (event.kind === MediaDeviceType.audioinput) { this.bindDevice() }
		})

		globalEvents.on('MediaDeviceSelected', data => {
			if (data.type === MediaDeviceType.audioinput) { this.bindDevice() }
		})

		globalEvents.on('StatusRequest', () => {
			globalEvents.emit('StatusReport', { key: 'yealink', value: this.use.value })
			globalEvents.emit('StatusReport', { key: 'yealinkIntegration', value: 'Electron SDK' })
			if (this.use.value) {
				globalEvents.emit('StatusReport', { key: 'yealinkDevice', value: this.useDeviceName.value as string })
			}
		})

		// Keep the device phonebook in sync with colleagues and the phonebook
		globalEvents.on('Phonebook:Reloaded', () => { this.schedulePhonebookSync() })
		globalEvents.on('Phonebook:Created', () => { this.schedulePhonebookSync() })
		globalEvents.on('Phonebook:Updated', () => { this.schedulePhonebookSync() })
		globalEvents.on('Phonebook:Deleted', () => { this.schedulePhonebookSync() })
		watch(() => useEmployeesStore().employees.length, () => { this.schedulePhonebookSync() })
		watch(() => usePhonebookStore().contacts.length, () => { this.schedulePhonebookSync() })

		this.start()
	}

	/** Get the YealinkElectron singleton instance */
	static get instance() {
		return YealinkElectron.#instance
	}

	/** Create the YealinkElectron singleton instance and return it */
	static create(eventCallback: (event: YealinkHeadsetEvent, value?: string) => void) {
		if (!YealinkElectron.#instance) {
			YealinkElectron.#instance = new YealinkElectron()
		}
		YealinkElectron.#instance.eventCallback = eventCallback
		return YealinkElectron.#instance
	}

	/** True when the app is embedded via IPC in a desktop shell that carries the Yealink SDK (v29+) */
	static isAvailable() {
		const ss = useSettingsStore()
		const ipc = (window as unknown as WindowElectronAPI).electronAPI
		const hasBridge = typeof ipc?.yealinkStart === 'function'
		const available = ss.runtime.isEmbeddedIpc && hasBridge

		if (available) {
			logger.info('YealinkElectron: Yealink headsets are driven through the native SDK in the desktop shell')
		} else {
			logger.info(`YealinkElectron: Native SDK integration not available (embedded via IPC: ${ss.runtime.isEmbeddedIpc}, shell has Yealink bridge: ${hasBridge}) - Yealink headsets will use WebHID`)
		}

		return available
	}

	// #region Init

	/** Subscribe to events and start the SDK in the main process */
	private async start() {
		// Subscribe BEFORE starting so no deviceAttached events are missed
		this.ipc.onYealink(event => { this.onYealinkEvent(event) })

		const started = await this.ipc.yealinkStart()

		if (!started.success) {
			logger.error(`YealinkElectron: Failed to start the Yealink SDK - ${started.error?.message}`)
			return
		}

		logger.info(`YealinkElectron: SDK ${started.result?.sdkVersion} started - ${started.result?.devices.length ?? 0} device(s) attached`)

		// The start result is the source of truth (also after a frontend reload)
		this.devices.clear()

		for (const device of started.result?.devices ?? []) {
			this.devices.set(device.info.deviceId, device)
			logger.info(`YealinkElectron: Attached: ${device.info.name} (deviceId ${device.info.deviceId})`)
		}
		this.bindDevice()
	}

	/**
	 * Bind call control to the attached device that matches the SELECTED audio device (matched on
	 * its USB product id). With two Yealinks connected, picking an arbitrary device would bind call
	 * control to whichever enumerated first - possibly the wrong headset.
	 */
	private bindDevice() {
		const mediaDevices = useMediaDevicesStore()
		const microphone = mediaDevices.microphoneDevice
		let device: YealinkDeviceSnapshot | undefined = undefined
		let noBindReason = ''

		if (microphone && isYealinkMediaDevice(microphone)) {
			const candidates = [...this.devices.values()].filter(candidate => !candidate.info.isInFirmwareUpdateMode)
			const matched = findMatchingYealinkHid(
				candidates.map(candidate => ({ vendorId: candidate.info.vendorId, productId: candidate.info.productId, productName: candidate.info.name, snapshot: candidate })),
				parseUsbProductId(microphone.productId)
			)

			// The audio interface can report a different product id than the SDK - with a single device attached it is safe to bind anyway
			device = matched?.snapshot ?? (candidates.length === 1 ? candidates[0] : undefined)

			if (!device) {
				noBindReason = `the selected microphone (${microphone.label}) is a Yealink but the SDK reports ` +
					(this.devices.size === 0 ? 'no attached devices' : `${this.devices.size} attached device(s), none matching it`)
			}
		} else if (this.devices.size > 0) {
			noBindReason = `${this.devices.size} Yealink device(s) attached but the selected microphone (${microphone?.label ?? 'none'}) is not a Yealink`
		}

		// Say why call control is off whenever there is something to say - but never repeat it (the
		// steady no-devices/no-Yealink-microphone state stays silent)
		if (!device && (noBindReason || this.activeDeviceId !== undefined)) {
			this.logBindOutcome(`YealinkElectron: Call control disabled - ${noBindReason || 'no Yealink devices attached'}`)
		}

		if (device?.info.deviceId === this.activeDeviceId) { return }

		// Release any call left on the device we are leaving
		if (this.activeDeviceId !== undefined && this.cid !== undefined) {
			void this.ipc.yealinkTerminateCall(this.activeDeviceId, this.cid)
		}
		this.resetCallState()

		this.activeDeviceId = device?.info.deviceId
		this.use.value = !!device
		this.useDeviceName.value = device ? (device.info.isDongle && device.info.dongleName ? `${device.info.name} (${device.info.dongleName})` : device.info.name) : undefined

		if (device) {
			this.logBindOutcome(`YealinkElectron: Headset connected: ${this.useDeviceName.value} - deviceId ${device.info.deviceId}`)
			this.lastPushedPhonebook = '' // force a push to the newly bound device
			this.schedulePhonebookSync()
		}
	}

	/** Log a bind outcome, suppressing consecutive repeats of the same message */
	private logBindOutcome(message: string) {
		if (message === this.lastBindLog) { return }
		this.lastBindLog = message
		logger.info(message)
	}

	// #endregion

	// #region Events from the device

	private onYealinkEvent(event: YealinkEvent) {
		switch (event.type) {
			case 'deviceAttached':
				logger.info(`YealinkElectron: Device attached: ${event.device.info.name} (deviceId ${event.device.info.deviceId})`)
				this.devices.set(event.device.info.deviceId, event.device)
				this.bindDevice()
				break
			case 'deviceRemoved':
				logger.info(`YealinkElectron: Device removed: deviceId ${event.deviceId}`)
				this.devices.delete(event.deviceId)
				this.bindDevice()
				break
			case 'callAction':
				this.onCallAction(event)
				break
			case 'batteryUpdate':
				if (event.low && !event.charging) {
					logger.info(`YealinkElectron: Headset battery is low - ${event.levelPercent}%`)
				}
				break
			default: // scanComplete, dialNumberChanged, hidInput, hidRaw, busylight, focusChange, firmwareProgress, settingsChanged - not used
				break
		}
	}

	/**
	 * The user pressed a call-control button on the device. This is a request, not a notification -
	 * we forward it to the SipClient, and the SDK is acknowledged when the SipClient calls back into
	 * offHook/onHook/hold/unhold/mute/unmute after applying the action to the SIP call.
	 */
	private onCallAction(event: YealinkCallActionEvent) {
		if (event.deviceId !== this.activeDeviceId) {
			logger.debug(`YealinkElectron: Ignoring ${event.actionName} from unbound device ${event.deviceId}`)
			return
		}

		logger.info(`YealinkElectron: User pressed ${event.actionName}`)

		switch (event.actionName) {
			case 'Accept':
				this.eventCallback('ANSWER')
				break
			case 'Reject':
			case 'Terminate': {
				// Acknowledge right away so the device always leaves the call state - even if no SIP call remains.
				// The later onHook() from the SipClient becomes a no-op because the cid is already cleared.
				const cid = this.cid

				this.resetCallState()
				if (cid !== undefined) { void this.ipc.yealinkTerminateCall(event.deviceId, cid) }
				this.eventCallback(event.actionName === 'Reject' ? 'REJECT' : 'HANGUP')
				break
			}
			case 'Hold':
				this.eventCallback('HOLD')
				break
			case 'Resume':
				this.eventCallback('UNHOLD')
				break
			case 'Mute':
				this.eventCallback('MUTE')
				break
			case 'Unmute':
				this.eventCallback('UNMUTE')
				break
			case 'DTMF':
				if (event.digit !== undefined) { this.eventCallback('DTMF', event.digit) }
				break
			case 'CallOut':
				this.onDeviceCallOut(event)
				break
			default: // EnterDial, Redial - not used
				break
		}
	}

	/** The user dialed a number on the device (dialpad, phonebook or redial) */
	private onDeviceCallOut(event: YealinkCallActionEvent) {
		if (!event.number) {
			logger.info('YealinkElectron: CallOut without a number (phonebook/redial edge case) - ignoring')
			return
		}

		const ss = useSettingsStore()
		const digits = event.number.replace(/\D/g, '')
		let number = event.number
		let name: string | undefined = undefined

		// The device sends digits only - a leading + is lost. Picking a contact on the device
		// auto-types one of its stored numbers, so correlate back to the pushed contact to dial
		// the full number and show the right name.
		for (const contact of this.pushedContacts) {
			const match = [contact.number, contact.mobile, contact.other].find(num => num && num.replace(/\D/g, '') === digits)

			if (match) {
				number = match
				name = contact.name
				break
			}
		}

		if (number.startsWith('00')) { number = '+' + number.substring(2) }
		if (!number.startsWith('+') && number.length > 7 && ss.pbxSettings?.nationalPrefix) { number = ss.pbxSettings.nationalPrefix + number }

		logger.info(`YealinkElectron: User dialed ${event.number} on the device${number !== event.number ? ` - resolved to ${name ? name + ' ' : ''}${number}` : ''}`)

		// Never two calls on the device at once - end any call still shown before starting the new one
		if (this.cid !== undefined) {
			void this.ipc.yealinkTerminateCall(event.deviceId, this.cid)
		}

		// Move the device from dial-entry to ringback right away - the SIP progress will find the ringback state already set
		this.cid = this.nextCid()
		this.callState = 'ringback'
		this.currentContact = this.makeContact(name, number)
		void this.ipc.yealinkRingBack(event.deviceId, this.cid)

		globalEvents.emit('Call:Dial', { number })
	}

	// #endregion

	// #region Actions - public (called by the SipClient)

	/**
	 * Ring the headset for an incoming call, showing the caller on the display
	 * @param name Caller name, if known
	 * @param number Caller number, if known
	 */
	async ringOn(name?: string, number?: string) {
		const deviceId = this.activeDeviceId

		if (deviceId === undefined) { return }

		const contact = this.makeContact(name, number)

		if (this.callState === 'ringing' && this.cid !== undefined) {
			// Already ringing - just refresh the caller shown on the display
			this.currentContact = contact
			void this.ipc.yealinkUpdateContact(deviceId, this.cid, contact)
			return
		}

		if (this.cid !== undefined) {
			// Never two calls on the device at once - end the stale one first
			await this.ipc.yealinkTerminateCall(deviceId, this.cid)
		}

		this.cid = this.nextCid()
		this.callState = 'ringing'
		this.currentContact = contact
		logger.info(`YealinkElectron: Telling headset to start ringer - ${contact.name} ${contact.number}`)

		const res = await this.ipc.yealinkIncomingCall(deviceId, this.cid, contact)

		if (!res.success) { logger.error(`YealinkElectron: Start ringer failed - ${res.error?.message}`) }
	}

	async ringOff() {
		if (this.callState === 'ringing') { await this.onHook() }
	}

	/**
	 * Take the headset off hook. Depending on the current state this answers the ringing call,
	 * starts ringback for a new outgoing call or transitions an outgoing call to active.
	 * @param name Name of the other party, if known
	 * @param number Number of the other party, if known
	 * @param answered True when the call has been answered (accepted), false while still in progress
	 */
	async offHook(name?: string, number?: string, answered = false) {
		const deviceId = this.activeDeviceId

		if (deviceId === undefined) { return }

		const contact = this.makeContact(name, number)

		logger.info(`YealinkElectron: Telling headset to go off hook (${this.callState})`)

		switch (this.callState) {
			case 'ringing': {
				const cid = this.cid as number

				this.callState = 'active'
				void this.ipc.yealinkAnswerCall(deviceId, cid)

				// If another call than the ringing one was answered, refresh the display
				if ((contact.name || contact.number) && (contact.name !== this.currentContact.name || contact.number !== this.currentContact.number)) {
					this.currentContact = contact
					void this.ipc.yealinkUpdateContact(deviceId, cid, contact)
				}
				break
			}
			case 'ringback':
				if (answered) {
					this.callState = 'active'
					// Keep a resolved contact (e.g. from a device phonebook dial) unless a real name is provided
					if (contact.name && contact.name !== contact.number) { this.currentContact = contact }
					void this.ipc.yealinkOutgoingCall(deviceId, this.cid as number, this.currentContact)
				}
				// else: still in ringback (SIP 183 and 180 can both fire) - nothing to do
				break
			case 'idle':
				this.cid = this.nextCid()
				this.currentContact = contact
				if (answered) {
					this.callState = 'active'
					void this.ipc.yealinkOutgoingCall(deviceId, this.cid, contact)
				} else {
					this.callState = 'ringback'
					void this.ipc.yealinkRingBack(deviceId, this.cid)
				}
				break
			case 'active':
			case 'hold':
				break // Already off hook
		}
	}

	/** Put the headset on hook - ends the call shown on the device */
	async onHook() {
		const deviceId = this.activeDeviceId
		const cid = this.cid

		this.resetCallState()
		if (deviceId === undefined || cid === undefined) { return }

		logger.info('YealinkElectron: Telling headset to go on hook')

		const res = await this.ipc.yealinkTerminateCall(deviceId, cid)

		if (!res.success) { logger.error(`YealinkElectron: On hook failed - ${res.error?.message}`) }
	}

	async hold() {
		const deviceId = this.activeDeviceId

		if (deviceId === undefined || this.cid === undefined) { return }

		logger.info('YealinkElectron: Telling headset to go on hold')
		this.callState = 'hold'

		const res = await this.ipc.yealinkHoldCall(deviceId, this.cid, this.currentContact)

		if (!res.success) { logger.error(`YealinkElectron: Hold failed - ${res.error?.message}`) }
	}

	async unhold() {
		const deviceId = this.activeDeviceId

		if (deviceId === undefined || this.cid === undefined) { return }

		logger.info('YealinkElectron: Telling headset to unhold')
		this.callState = 'active'

		const res = await this.ipc.yealinkResumeCall(deviceId, this.cid)

		if (!res.success) { logger.error(`YealinkElectron: Unhold failed - ${res.error?.message}`) }
	}

	/** Show the call as muted on the device - also the required ack when the user pressed Mute on the device */
	async mute() {
		if (this.activeDeviceId === undefined || this.cid === undefined) { return }
		await this.ipc.yealinkMuteCall(this.activeDeviceId, this.cid)
	}

	/** Show the call as unmuted on the device - also the required ack when the user pressed Unmute on the device */
	async unmute() {
		if (this.activeDeviceId === undefined || this.cid === undefined) { return }
		await this.ipc.yealinkUnmuteCall(this.activeDeviceId, this.cid)
	}

	// #endregion

	// #region Phonebook

	private schedulePhonebookSync() {
		if (this.phonebookTimer) { clearTimeout(this.phonebookTimer) }
		this.phonebookTimer = setTimeout(() => {
			this.phonebookTimer = undefined
			this.syncPhonebook()
		}, PHONEBOOK_SYNC_DEBOUNCE_MS)
	}

	/**
	 * Push contacts to the phonebook browsable on the device: local extensions (colleagues) first,
	 * then the user's personal phonebook, then fill up with the shared phonebook - capped at
	 * MAX_PHONEBOOK_ENTRIES because the device silently drops any overflow.
	 */
	private async syncPhonebook() {
		if (this.phonebookSyncRunning) {
			this.schedulePhonebookSync()
			return
		}

		const deviceId = this.activeDeviceId

		if (deviceId === undefined) {
			logger.debug('YealinkElectron: Phonebook sync skipped - no headset bound for call control')
			return
		}

		// The SDK's Contact feature flag is unreliable (reports unsupported on devices that do have
		// a phonebook) - assume every Yealink headset supports one and just push

		this.phonebookSyncRunning = true

		try {
			const employees = useEmployeesStore()
			const phonebook = usePhonebookStore()

			await employees.ready()
			await phonebook.ready()

			const contacts: YealinkContact[] = []

			// 1. Local extensions (colleagues)
			for (const employee of [...employees.employees].sort((a, b) => a.name.localeCompare(b.name))) {
				if (employee.me) { continue }

				const extension = employee.primaryExtension
				const number = extension?.number ?? extension?.direct ?? extension?.mobile[0]

				if (!employee.name || !extension || !number) { continue }

				contacts.push({
					name: employee.name,
					number: number,
					mobile: extension.mobile[0],
					other: extension.direct !== number ? extension.direct : undefined
				})
			}

			const colleagueCount = contacts.length

			// 2. The user's personal phonebook, 3. then fill up with the shared phonebook
			const personal = phonebook.contacts.filter(contact => contact.employee)
			const shared = phonebook.contacts.filter(contact => !contact.employee)
			let personalCount = 0

			for (const contact of [...personal, ...shared]) {
				const entry = this.phonebookEntryFrom(contact)

				if (entry) {
					contacts.push(entry)
					if (contact.employee) { personalCount++ }
				}
			}

			const list = contacts.slice(0, MAX_PHONEBOOK_ENTRIES)
			const fingerprint = `${deviceId}:${JSON.stringify(list)}`

			if (fingerprint === this.lastPushedPhonebook) {
				logger.debug(`YealinkElectron: Phonebook unchanged (${list.length} contacts) - not pushing to the device`)
				return
			}

			logger.info(`YealinkElectron: Pushing ${list.length} contacts to the phonebook on ${this.useDeviceName.value}...`)

			// Never sync an empty list first to clear the device: a WH66 discards a contact sync that
			// arrives while it is still wiping, leaving the phonebook empty. Replace semantics belong
			// to the shell's SDK (tcx-yealink-headset 1.0.1+), which clears and pushes back-to-back
			// in a way the device does handle - shells on 1.0.0 append instead and must be updated.
			const res = await this.ipc.yealinkSetPhonebook(deviceId, list)

			if (res.success) {
				this.pushedContacts = list
				this.lastPushedPhonebook = fingerprint
				logger.info(`YealinkElectron: Pushed ${list.length} contacts to the device phonebook ` +
					`(${Math.min(colleagueCount, list.length)} colleagues, ${Math.min(personalCount, Math.max(list.length - colleagueCount, 0))} personal, ` +
					`${Math.max(list.length - colleagueCount - personalCount, 0)} shared${contacts.length > list.length ? ` - ${contacts.length - list.length} dropped due to the device limit` : ''})`)
			} else {
				logger.error(`YealinkElectron: Failed to push the phonebook to the device - ${res.error?.message}`)
			}
		}
		catch (err) {
			logger.error(`YealinkElectron: Phonebook sync failed - ${(err as Error).message}`)
		}
		finally {
			this.phonebookSyncRunning = false
		}
	}

	/**
	 * Map a phonebook contact to a device phonebook entry
	 * @param contact Phonebook contact to map
	 * @returns The device entry, or undefined when the contact has no name or no number
	 */
	private phonebookEntryFrom(contact: PbxPhonebookContact): YealinkContact | undefined {
		if (!contact.name) { return undefined }

		const numbers = contact.phoneNumbers.filter(phoneNumber => phoneNumber.number)
		const main = numbers.find(phoneNumber => phoneNumber.type === PbxPhonebookContactPhoneNumberType.MAIN) ??
			numbers.find(phoneNumber => phoneNumber.type === PbxPhonebookContactPhoneNumberType.WORK) ??
			numbers.find(phoneNumber => phoneNumber.type !== PbxPhonebookContactPhoneNumberType.MOBILE) ??
			numbers[0]

		if (!main) { return undefined }

		const mobile = numbers.find(phoneNumber => phoneNumber.type === PbxPhonebookContactPhoneNumberType.MOBILE && phoneNumber !== main)
		const other = numbers.find(phoneNumber => phoneNumber !== main && phoneNumber !== mobile)

		return {
			name: contact.name,
			number: main.number,
			company: contact.company ?? undefined,
			mobile: mobile?.number,
			other: other?.number
		}
	}

	// #endregion

	// #region Helpers

	private nextCid() {
		this.cidCounter += 1
		return this.cidCounter
	}

	private resetCallState() {
		this.cid = undefined
		this.callState = 'idle'
		this.currentContact = { name: '', number: '' }
	}

	/** Build a contact for the device display - the device draws the avatar initials from the name, so fall back to the number */
	private makeContact(name?: string, number?: string): YealinkContact {
		return { name: name?.trim() || number || '', number: number ?? '' }
	}

	// #endregion
}
